import { Component, inject, signal, OnInit, OnDestroy, PLATFORM_ID } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { Subscription } from 'rxjs';
import { CardModule } from 'primeng/card';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { TooltipModule } from 'primeng/tooltip';
import { AccordionModule } from 'primeng/accordion';
import { DividerModule } from 'primeng/divider';
import { SelectModule } from 'primeng/select';

import {
  PartnerDashboardService,
  MemberLookupQuery,
  MemberLookupResult,
  MemberLookupMember,
} from '../../services/partner-dashboard';
import { PartnerService } from '../../services/partner';
import { AuthService } from '../../services/auth';
import { ToastService } from '../../services/toast';
import { PageTitleService } from '../../services/page-title';
import { IndDatePipe } from '../../pipes/ind-date';
import { PAY, KYC, Severity } from './status-severity';

@Component({
  selector: 'app-member-lookup',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterLink,
    CardModule,
    TableModule,
    TagModule,
    ButtonModule,
    InputTextModule,
    IconFieldModule,
    InputIconModule,
    TooltipModule,
    AccordionModule,
    DividerModule,
    SelectModule,
    IndDatePipe,
  ],
  templateUrl: './member-lookup.html',
  styleUrls: ['./member-lookup.css'],
})
export class MemberLookup implements OnInit, OnDestroy {
  private svc = inject(PartnerDashboardService);
  private partnerSvc = inject(PartnerService);
  private auth = inject(AuthService);
  private toast = inject(ToastService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private platformId = inject(PLATFORM_ID);
  readonly pageTitle = inject(PageTitleService).title;
  private qpSub?: Subscription;

  readonly isBrowser = isPlatformBrowser(this.platformId);
  readonly isAdmin = this.auth.isAdmin();

  query = signal('');
  loading = signal(false);
  searched = signal(false);
  results = signal<MemberLookupResult[]>([]);
  /** Which accordion panels are open (auto-expand the single match). */
  accordionValue = signal<number[]>([]);

  // advanced exact-field search
  advancedOpen = signal(false);
  advMemberId = signal<number | null>(null);
  advLoanNumber = signal('');
  advUniqueId = signal('');
  advMobile = signal('');

  // admin partner restriction
  selPartnerId = signal<number | null>(null);
  partnerOptions = signal<{ label: string; value: number }[]>([]);

  ngOnInit() {
    if (!this.isBrowser) return;
    if (this.isAdmin) this.loadPartners();
    // Deep link (global search): /member-lookup?q=<id | uid | series no | mobile> —
    // subscribed so a NEW spotlight pick re-runs the search while already on this page.
    this.qpSub = this.route.queryParamMap.subscribe((pm) => {
      const q = (pm.get('q') || '').trim();
      if (q && q !== this.query()) {
        this.advancedOpen.set(false);
        this.query.set(q);
        this.search();
      }
    });
  }

  ngOnDestroy() {
    this.qpSub?.unsubscribe();
  }

  search() {
    if (!this.isBrowser) return;
    const q: MemberLookupQuery = {};
    if (this.isAdmin && this.selPartnerId()) q.partnerId = this.selPartnerId()!;

    if (this.advancedOpen()) {
      if (this.advMemberId()) q.memberId = Number(this.advMemberId());
      if (this.advLoanNumber().trim()) q.loanNumber = this.advLoanNumber().trim();
      if (this.advUniqueId().trim()) q.memberUniqueId = this.advUniqueId().trim();
      if (this.advMobile().trim()) q.mobileNumber = this.advMobile().trim();
    } else {
      const term = this.query().trim();
      if (term) q.query = term;
    }

    const hasIdentifier =
      q.query || q.memberId || q.loanNumber || q.memberUniqueId || q.mobileNumber;
    if (!hasIdentifier) {
      this.toast.warning('Enter something to search');
      return;
    }

    this.loading.set(true);
    this.svc.memberLookup(q).subscribe({
      next: (res: any) => {
        this.loading.set(false);
        this.searched.set(true);
        if (res?.success) {
          const data: MemberLookupResult[] = Array.isArray(res.data) ? res.data : [];
          this.results.set(data);
          this.accordionValue.set(data.length === 1 ? [0] : []);
        } else {
          this.results.set([]);
          this.toast.error(res?.message || 'Member lookup failed');
        }
      },
      error: () => {
        this.loading.set(false);
        this.searched.set(true);
        this.results.set([]);
      },
    });
  }

  toggleAdvanced() {
    this.advancedOpen.update((v) => !v);
  }

  private loadPartners() {
    this.partnerSvc.getAll().subscribe({
      next: (res: any) => {
        const data = res?.data ?? res ?? [];
        const list = Array.isArray(data) ? data : [];
        this.partnerOptions.set(
          list.map((p: any) => ({
            label: p.partnerName ?? p.partnerCode ?? `Partner #${p.id}`,
            value: p.id,
          })),
        );
      },
      error: () => {},
    });
  }

  // ===================== display helpers =====================
  inr(n: number | null | undefined): string {
    return '₹' + Number(n ?? 0).toLocaleString('en-IN', { maximumFractionDigits: 0 });
  }

  paySeverity(s?: string | null): Severity {
    return (PAY[(s || '').toUpperCase()] ?? 'secondary') as Severity;
  }
  kycSeverity(s?: string | null): Severity {
    return (KYC[(s || '').toUpperCase()] ?? 'secondary') as Severity;
  }

  initials(name?: string | null): string {
    const parts = (name || '').trim().split(/\s+/).filter(Boolean);
    if (!parts.length) return 'M';
    return (parts[0][0] + (parts[1]?.[0] ?? '')).toUpperCase();
  }

  addr(m: MemberLookupMember): string {
    return [m.address, m.city, m.state, m.pincode].filter(Boolean).join(', ') || '—';
  }

  /** Open the full Payment Details page for a payment-history row (any status). */
  openPaymentDetail(p: any) {
    if (p?.id) this.router.navigate(['/payments/detail', p.id]);
  }
}
